<template>
  <div class="relative" ref="menuRef">
    <!-- User menu button -->
    <button
      @click="isOpen = !isOpen"
      class="flex items-center space-x-2 p-2 text-gray-400 dark:text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
    >
      <div class="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center">
        <span class="text-sm font-medium text-white">
          {{ userInitials }}
        </span>
      </div>
      <!-- Show user name on larger screens -->
      <div class="hidden sm:block text-left">
        <p class="text-sm font-medium text-gray-900 dark:text-white dark:text-gray-100">
          {{ authStore.user?.name }}
        </p>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          {{ currentPlan?.name || 'Trial' }}
        </p>
      </div>
      <ChevronDownIcon 
        class="w-4 h-4 transition-transform duration-200"
        :class="isOpen ? 'rotate-180' : ''"
      />
    </button>
    
    <!-- Dropdown menu -->
    <Transition name="dropdown">
      <div
        v-if="isOpen"
        class="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-2 z-50"
      >
        <!-- User info -->
        <div class="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
              <span class="text-sm font-medium text-white">
                {{ userInitials }}
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-white dark:text-gray-100 truncate">
                {{ authStore.user?.name }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400 truncate">
                {{ authStore.user?.email }}
              </p>
            </div>
          </div>

          <!-- Subscription status -->
          <div class="mt-3 p-2 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/30 rounded-lg">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs font-medium text-primary-700 dark:text-primary-300">
                  {{ subscriptionPlan?.name || 'Trial Account' }}
                </p>
                <p class="text-xs text-primary-600 dark:text-primary-400">
                  {{ authStore.getRemainingEmails() }} emails remaining
                </p>
              </div>
              <button
                v-if="!authStore.user?.is_on_trial"
                @click="viewBilling"
                class="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
              >
                Manage
              </button>
            </div>
          </div>
        </div>
        
        <!-- Menu items -->
        <div class="py-2">
          <UserMenuItem
            :icon="UserIcon"
            label="Profile Settings"
            @click="goToProfile"
          />
          
          <UserMenuItem
            :icon="CogIcon"
            label="Account Settings"
            @click="goToSettings"
          />
          
          <UserMenuItem
            :icon="CreditCardIcon"
            label="Billing & Subscription"
            @click="viewBilling"
          />
          
          <UserMenuItem
            :icon="KeyIcon"
            label="API Keys"
            @click="viewApiKeys"
          />
          
          <div class="border-t border-gray-100 dark:border-gray-700 my-2"></div>

          <UserMenuItem
            :icon="QuestionMarkCircleIcon"
            label="Help & Support"
            @click="openSupport"
          />

          <UserMenuItem
            :icon="DocumentTextIcon"
            label="Documentation"
            @click="openDocs"
          />

          <div class="border-t border-gray-100 dark:border-gray-700 my-2"></div>
          
          <UserMenuItem
            :icon="ArrowRightOnRectangleIcon"
            label="Sign Out"
            variant="danger"
            @click="handleLogout"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import UserMenuItem from './UserMenuItem.vue'

import {
  ChevronDownIcon,
  UserIcon,
  CogIcon,
  CreditCardIcon,
  KeyIcon,
  QuestionMarkCircleIcon,
  DocumentTextIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/vue/24/outline'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()

const isOpen = ref(false)
const menuRef = ref(null)

// Helper to add locale to route
const withLocale = (routeObj) => {
  const locale = route.params.locale || 'en'
  return { ...routeObj, params: { ...routeObj.params, locale } }
}

const userInitials = computed(() => {
  const name = authStore.user?.name || ''
  return name.split(' ').map(n => n[0]).join('').toUpperCase()
})

const subscriptionPlan = computed(() => authStore.subscriptionPlan)
const currentPlan = computed(() => authStore.subscriptionPlan)

// Close menu when clicking outside
const handleClickOutside = (event) => {
  if (menuRef.value && !menuRef.value.contains(event.target)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Menu actions
const goToProfile = () => {
  router.push(withLocale({ name: 'profile' }))
  isOpen.value = false
}

const goToSettings = () => {
  router.push(withLocale({ name: 'settings' }))
  isOpen.value = false
}

const viewBilling = () => {
  // Navigate to billing tab in settings
  router.push(withLocale({ name: 'settings', hash: '#billing' }))
  isOpen.value = false
}

const viewApiKeys = () => {
  // Navigate to API keys section
  router.push(withLocale({ name: 'settings', hash: '#api-keys' }))
  isOpen.value = false
}

const openSupport = () => {
  // Open support chat or email
  window.open('mailto:support@mailerx.com', '_blank')
  isOpen.value = false
}

const openDocs = () => {
  router.push(withLocale({ name: 'api-docs' }))
  isOpen.value = false
}

const handleLogout = async () => {
  try {
    await authStore.logout()
  } catch (error) {
    console.error('Logout error:', error)
  }
  isOpen.value = false
}
</script>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}
</style>